import React, { useState, useMemo } from "react";
import { Combobox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, PlusIcon } from "@heroicons/react/20/solid";
import { useTranslation } from 'react-i18next';
import AddCategoryGroupModal from '../Modals/AddCategoryGroupModal';
import AddCategoryModal from '../Modals/AddCategoryModal';

export function EnhancedCategoryCombobox({
  groupedCategories = [],
  categoriesWithoutGroup = [],
  value,
  onChange,
  placeholder = "Выберите категорию...",
  filterPlaceholder = "Поиск по названию категории...",
  noCategoryLabel = "Без категории",
  className = "",
  showCreateOptions = true,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [selectedGroupForCategory, setSelectedGroupForCategory] = useState(null);
  

  // Собираем все категории в один массив с инфой о группе
  const allOptions = useMemo(() => [
    { id: "none", name: noCategoryLabel, group: null },
    ...groupedCategories.flatMap(({ group, categories }) =>
      categories.map((cat) => ({ ...cat, group: group.name }))
    ),
    ...categoriesWithoutGroup.map((cat) => ({ ...cat, group: null })),
  ], [groupedCategories, categoriesWithoutGroup, noCategoryLabel]);

  // Фильтрация
  const filteredOptions =
    query === ""
      ? allOptions
      : allOptions.filter((cat) =>
          cat.name.toLowerCase().includes(query.toLowerCase())
        );

  // Найти выбранную категорию
  const selected = allOptions.find((cat) => String(cat.id) === String(value)) || allOptions[0];

  // Группировка для отображения
  const grouped = useMemo(() => {
    const groups = {};
    filteredOptions.forEach((cat) => {
      const group = cat.group || "__none__";
      if (!groups[group]) groups[group] = [];
      groups[group].push(cat);
    });
    return groups;
  }, [filteredOptions]);

  // Обработчики для модальных окон
  const handleAddGroup = () => {
    setShowAddGroupModal(true);
  };

  const handleAddCategory = (groupId, groupName) => {
    setSelectedGroupForCategory({ id: groupId, name: groupName });
    setShowAddCategoryModal(true);
  };

  const handleModalSuccess = () => {
    // Модальные окна сами обновят кэш через мутации
  };

  return (
    <Combobox value={selected.id} onChange={onChange} as="div" className={className}>
      <div className="relative">
        <Combobox.Button className="w-full">
          <Combobox.Input
            className="w-full px-3 py-2 text-sm border border-gray-600 dark:border-gray-600 focus:border-blue-600 dark:focus:border-blue-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-h-[40px]"
            displayValue={(id) => {
              if (String(id) === "none") return "";
              const cat = allOptions.find((c) => String(c.id) === String(id));
              return cat
                ? cat.group
                  ? `${cat.name} (${cat.group})`
                  : cat.name
                : "";
            }}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={String(selected.id) === "none" ? noCategoryLabel : filterPlaceholder}
            autoComplete="off"
          />
          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </Combobox.Button>
        
        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm border border-gray-200 dark:border-gray-700">
          
          
          {/* Список категорий */}
          {Object.entries(grouped).map(([group, cats]) => {
            // Находим ID группы для кнопки добавления
            const groupId = group !== "__none__" ? 
              groupedCategories.find(g => g.group.name === group)?.group.id : null;
            
            return (
              <React.Fragment key={group}>
                {group !== "__none__" && (
                  <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 font-semibold select-none">
                    {group}
                  </div>
                )}
                {cats.map((cat) => (
                  <Combobox.Option
                    key={cat.id}
                    value={cat.id}
                    className={({ active, selected }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 text-sm ${
                        active ? "bg-blue-100 dark:bg-blue-900/20 text-blue-900 dark:text-blue-200" : "text-gray-900 dark:text-gray-100"
                      }`
                    }
                  >
                    {({ selected, active }) => (
                      <>
                        <span className={`block truncate ${selected ? "font-semibold" : "font-normal"}`}>
                          {cat.name}
                          {cat.group ? ` (${cat.group})` : ""}
                        </span>
                        {selected ? (
                          <span
                            className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? "text-blue-600" : "text-blue-600"}`}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Combobox.Option>
                ))}
                
                {/* Кнопка добавления категории в группу */}
                {showCreateOptions && group !== "__none__" && groupId && (
                  <div className="px-3 py-1">
                    <button
                      onClick={() => handleAddCategory(groupId, group)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-green-500 text-white rounded hover:bg-green-600"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Добавить категорию
                    </button>
                  </div>
                )}
              </React.Fragment>
            );
          })}
          
          {/* Кнопка добавления новой группы в конце списка */}
          {showCreateOptions && (
            <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-600">
              <button
                onClick={handleAddGroup}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm bg-blue-400 text-white rounded hover:bg-blue-500"
              >
                <PlusIcon className="w-4 h-4" />
                Добавить новую группу категорий
              </button>
            </div>
          )}
          
          {filteredOptions.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400 select-none">Нет категорий</div>
          )}
        </Combobox.Options>
      </div>

      {/* Модальные окна */}
      <AddCategoryGroupModal
        isOpen={showAddGroupModal}
        onClose={() => setShowAddGroupModal(false)}
        onSuccess={handleModalSuccess}
      />
      
      <AddCategoryModal
        isOpen={showAddCategoryModal}
        onClose={() => setShowAddCategoryModal(false)}
        onSuccess={handleModalSuccess}
        groupName={selectedGroupForCategory?.name}
        groupId={selectedGroupForCategory?.id}
      />
    </Combobox>
  );
}
